import React, { useEffect, useState, useMemo } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { saveAs } from 'file-saver';
import { createOption, Option } from '../../../../utils';
import { Campaign } from '../../../../types/campaign';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { getDownloadBlockedReport, getDownloadGeneralReport, getDownloadVerificationReport } from '../../services/reportApi';
import Flatpickr from 'react-flatpickr';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface DownloadReportModalProps {
    campaign: Campaign;
    isOpen: boolean;
    toggle: () => void;
}

const DownloadReportModal = ({ campaign, isOpen, toggle }: DownloadReportModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [selectReportTypeValue, setSelectReportTypeValue] = useState<SingleValue<Option>>(createOption('Geral', 'general'));
    const [selectReportFormatValue, setSelectReportFormatValue] = useState<SingleValue<Option>>(createOption('xlsx', 'xlsx'));
    const selectReportTypeOptions = [
        createOption('Geral', 'general'),
        createOption('Verificação', 'verification'),
        createOption('Bloqueados', 'blocked'),
    ];
    const selectReportFormatOptions = [
        createOption('xlsx', 'xlsx'),
        createOption('csv', 'csv'),
        createOption('json', 'json'),
    ];

    const toggleModal = () => {
        toggle();
    };

    const selectReportTypeOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        setSelectReportTypeValue(newValue);
        handleForm.setFieldValue('type', newValue ? newValue.value : '');
    };

    const selectReportFormatOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        // This function is not used in the current implementation, but can be used if you want to handle format selection
        setSelectReportFormatValue(newValue);
        handleForm.setFieldValue('format', newValue ? newValue.value : '');
    };

    // Always keep rangeValue as an array of two dates or []
    const [rangeValue, setRangeValue] = useState<Date[]>([]);

    // Memoize the default periodo string based on rangeValue
    const defaultPeriodo = useMemo(() => {
        if (rangeValue.length === 2) {
            return rangeValue.map(d => d.toISOString().split('T')[0]).join(' - ');
        }
        return '';
    }, [rangeValue]);

    function getFileNameFromContentDisposition(header: string): string | null {
        const match = header.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
            return match[1];
        }
        return null;
    }

    const initialValues = {
        type: selectReportTypeValue?.value || 'general',
        periodo: defaultPeriodo,
        format: selectReportFormatValue?.value || 'xlsx',
        maxXlsxSizeMb: 10,
        compress: true,
        removeXlsxHeader: false, // Added field
    };

    const handleForm: any = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: Yup.object({
            type: Yup.string().required('O tipo do relatório é obrigatório'),
            format: Yup.string(),
            // Optionally add validation for removeXlsxHeader if needed
        }),
        onSubmit: (values: any) => {
            if (!values.periodo) {
                toast.error('Período é obrigatório');
                return;
            }
            setLoading(true);
            const [startDate, endDate] = values.periodo.split(' - ');
            values.startDate = startDate;
            values.endDate = endDate;
            const type = values.type || selectReportTypeValue?.value || 'general';
            delete values.periodo;
            delete values.type;
            if (type === 'general') {
                getDownloadGeneralReport(campaign.id, values)
                    .then((res) => {
                        const fileName = getFileNameFromContentDisposition(res.headers['content-disposition']);
                        if (fileName) {
                            saveAs(res.data, fileName);
                            toast.success('Relatório baixado com sucesso!');
                            toggleModal();
                        } else {
                            toast.error('Erro ao obter o nome do arquivo. Tente novamente.');
                        }
                    })
                    .catch((error: any) => {
                        toast.error('Erro ao baixar o relatório. Tente novamente.');
                        console.error('Download error:', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else if (type === 'verification') {
                getDownloadVerificationReport(campaign.id, values)
                    .then((res) => {
                        const fileName = getFileNameFromContentDisposition(res.headers['content-disposition']);
                        if (fileName) {
                            saveAs(res.data, fileName);
                            toast.success('Relatório baixado com sucesso!');
                            toggleModal();
                        } else {
                            toast.error('Erro ao obter o nome do arquivo. Tente novamente.');
                        }
                    })
                    .catch((error: any) => {
                        toast.error('Erro ao baixar o relatório. Tente novamente.');
                        console.error('Download error:', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else if (type === 'blocked') {
                getDownloadBlockedReport(campaign.id, values)
                    .then((res) => {
                        const fileName = getFileNameFromContentDisposition(res.headers['content-disposition']);
                        if (fileName) {
                            saveAs(res.data, fileName);
                            toast.success('Relatório baixado com sucesso!');
                            toggleModal();
                        } else {
                            toast.error('Erro ao obter o nome do arquivo. Tente novamente.');
                        }
                    })
                    .catch((error: any) => {
                        toast.error('Erro ao baixar o relatório. Tente novamente.');
                        console.error('Download error:', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    const handleRangeDatesChange = (newValue: Date[]) => {
        setRangeValue(newValue);
        handleForm.setFieldValue('periodo', newValue.map(d => d.toISOString().split('T')[0]).join(' - '));
    };

    useEffect(() => {
        // Find the earliest start date and latest end date from the campaign orders
        const startDate = campaign.orders.reduce(
            (min, o) => new Date(o.startDate) < new Date(min) ? o.startDate : min,
            campaign.orders[0].startDate
        );
        const endDate = campaign.orders.reduce(
            (max, o) => new Date(o.endDate) > new Date(max) ? o.endDate : max,
            campaign.orders[0].endDate
        );
        // Convert to local dates (avoid timezone issues)
        const localStartDate = new Date(new Date(startDate).setHours(0, 0, 0, 0));
        const localEndDate = new Date(new Date(endDate).setHours(23, 59, 59, 59));
        const dateRange = [localStartDate, localEndDate];
        setRangeValue(dateRange);
    }, [campaign.orders]);

    return (
        <Dialog open={isOpen} onOpenChange={toggleModal}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Baixar relatório</DialogTitle>
                </DialogHeader>

                <form onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleForm.handleSubmit();
                    return false;
                }}>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="type">
                                Tipo de relatório
                            </Label>
                            <Select
                                isClearable={true}
                                isSearchable={true}
                                name="type"
                                options={selectReportTypeOptions}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        borderColor: !!(handleForm.touched.type && handleForm.errors.type) ? 'red' : '#ced4da',
                                    }),
                                }}
                                onChange={selectReportTypeOnChange}
                                value={selectReportTypeValue}
                            />
                            {handleForm.touched.type && handleForm.errors.type && (
                                <p className="text-sm text-destructive">{handleForm.errors.type}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="periodo">
                                Período
                            </Label>
                            <Flatpickr
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                id="datepicker-publish-input"
                                placeholder="Selecione um período"
                                value={rangeValue}
                                onChange={handleRangeDatesChange}
                                options={{
                                    altInput: true,
                                    altFormat: "d/m/Y",
                                    mode: "range",
                                    dateFormat: "d/m/Y",
                                    minDate: new Date(new Date().setFullYear(new Date().getFullYear() - 2)),
                                    maxDate: new Date(),
                                }}
                            />
                            {handleForm.touched.periodo && handleForm.errors.periodo && (
                                <p className="text-sm text-destructive">{handleForm.errors.periodo}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="format">
                                Formato
                            </Label>
                            <Select
                                isClearable={true}
                                isSearchable={true}
                                name="format"
                                options={selectReportFormatOptions}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        borderColor: !!(handleForm.touched.format && handleForm.errors.format) ? 'red' : '#ced4da',
                                    }),
                                }}
                                onChange={selectReportFormatOnChange}
                                value={selectReportFormatValue}
                            />
                            {handleForm.touched.format && handleForm.errors.format && (
                                <p className="text-sm text-destructive">{handleForm.errors.format}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="maxXlsxSizeMb">
                                Tamanho máximo do output (MB)
                            </Label>
                            <Input
                                type="number"
                                name="maxXlsxSizeMb"
                                min={1}
                                value={handleForm.values.maxXlsxSizeMb}
                                onChange={handleForm.handleChange}
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="compress"
                                name="compress"
                                checked={handleForm.values.compress}
                                onChange={handleForm.handleChange}
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <Label htmlFor="compress" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Compactar relatório
                            </Label>
                        </div>

                        {handleForm.values.format === 'xlsx' && (
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="removeXlsxHeader"
                                    name="removeXlsxHeader"
                                    checked={handleForm.values.removeXlsxHeader}
                                    onChange={handleForm.handleChange}
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <Label htmlFor="removeXlsxHeader" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                    Remover cabeçalho do arquivo XLSX
                                </Label>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="submit"
                            className="ml-auto"
                        >
                            <i className="ri-arrow-right-line mr-2"></i>
                            {loading && <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>}
                            Baixar relatório
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default DownloadReportModal;
